import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import * as fromRoot from '../reducers';
import * as olci from '../actions/olci';

import { CONSTANTS } from '../constants';
import { ReservationRetrievalObject, Session } from '../models';
import { TelemetryContextService } from '@rccl/telemetry-angular';
import { BookingService } from '../modules/core/services/booking.service';
import { GatingServices } from '../modules/core/services/gating.services';

export const reservationGuard: CanActivateFn = (): Observable<boolean> => {

    const store: Store<fromRoot.AppState> = inject(Store<fromRoot.AppState>);
	const gatingService: GatingServices = inject(GatingServices);
	const bookingService: BookingService = inject(BookingService);
	const appContext: TelemetryContextService = inject(TelemetryContextService);
	const router: Router = inject(Router);
	const constants: any = inject(CONSTANTS);

	const olciDataCookie = gatingService.getOlciDataCookie();
	if (!olciDataCookie.vdsId) {
		(<any>window).location.href = gatingService.getGADashboardURL();
		return of(false);
	}
	const loggedInGuestInfo = {
		vdsDOB: olciDataCookie.dob,
		vdsEmail: olciDataCookie.email,
		vdsId: olciDataCookie.vdsId,
		vdsName: olciDataCookie.accountOwnerName,
	};

	store.dispatch(new olci.SetResponseErrorClearAction());
	store.dispatch(new olci.SessionTimestampRenewedAction());

	let reservationData: ReservationRetrievalObject = gatingService.getReservationData();
    if (olciDataCookie.bookingId) {
		// Different booking or different guest
		if (
			!reservationData ||
			reservationData.bookingId !== olciDataCookie.bookingId ||
			reservationData.lastName !== olciDataCookie.lastName ||
			reservationData.sailDate !== olciDataCookie.sailDate ||
			reservationData.vdsId !== loggedInGuestInfo.vdsId ||
			reservationData.locale !== olciDataCookie.locale
		) {
			store.dispatch(new olci.ClearStateOnlyAction());
      gatingService.saveLoggedInGuest(olciDataCookie.guestId);
      gatingService.setReservationData({
				brand: olciDataCookie.brand,
				bookingId: olciDataCookie.bookingId,
				departureYear: olciDataCookie.departureYear,
				departureMonth: olciDataCookie.departureMonth,
				departureDay: olciDataCookie.departureDay,
				sailDate: olciDataCookie.sailDate,
				shipCode: olciDataCookie.shipCode,
				lastName: olciDataCookie.lastName,
				vdsId: loggedInGuestInfo.vdsId,
				guestId: olciDataCookie.guestId,
				charterSailing: olciDataCookie.charterSailing,
				locale: olciDataCookie.locale,
				email: olciDataCookie.email,
				dob: olciDataCookie.dob,
				accountOwnerName: olciDataCookie.accountOwnerName,
			});
		}
	}

	reservationData = gatingService.getReservationData();
	if (!reservationData) {
		(<any>window).location.href = gatingService.getGADashboardURL();
		return of(false);
	}

	appContext.updateUserId(loggedInGuestInfo.vdsId);
	store.dispatch(new olci.StoreVdsInfoAction(loggedInGuestInfo));

	if (!bookingService.isInTheStore) {
		const payload = {
			sailingIds: [
				{
					shipCode: reservationData.shipCode,
					sailDate: reservationData.sailDate
				}
			]
		};
		store.dispatch(new olci.PostSailingInformationAction(payload));

		return store.pipe(
			select(fromRoot.getSessionState),
			filter((resData: Session) => resData.loadingSailingInfo === false),
			switchMap((resData: Session) => {
				if (resData.error) {
					router.navigate([constants.ROUTES.ERROR]);
					return of(false);
				} else {
					return of(true);
				}
			}),
			take(1)
		);
	} else {
		return of(true);
	}
}
